import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Activity, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/hooks/useTranslation";

interface TermsAcceptanceModalProps {
  userId: string;
  onAccepted: () => void;
}

export function TermsAcceptanceModal({ userId, onAccepted }: TermsAcceptanceModalProps) {
  const { translate } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!checked) return;
    setLoading(true);
    try {
      // Get current terms version from system_settings
      const { data: setting } = await supabase
        .from("system_settings")
        .select("value")
        .eq("key", "terms_version")
        .single();

      const currentVersion = setting?.value ?? "1.0";

      const { error } = await supabase
        .from("profiles")
        .update({
          terms_accepted_at: new Date().toISOString(),
          terms_version_accepted: currentVersion,
        })
        .eq("id", userId);

      if (error) throw error;
      onAccepted();
    } catch (err) {
      console.error("Failed to save terms acceptance:", err);
    } finally {
      setLoading(false);
    }
  }, [checked, userId, onAccepted]);

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 pb-4 flex flex-col items-center gap-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 text-primary">
            <Activity className="h-8 w-8" />
            <span className="text-2xl font-bold">AlphaSignal</span>
          </div>
          <h2 className="text-xl font-semibold text-foreground text-center">
            {translate("terms_modal_heading")}
          </h2>
          <p className="text-sm text-muted-foreground text-center">
            {translate("terms_modal_subheading")}
          </p>
        </div>

        {/* Scrollable summary */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 text-foreground font-medium">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <span>{translate("terms_modal_summary_title")}</span>
          </div>

          <p>{translate("terms_modal_summary_intro")}</p>

          <ul className="space-y-2 list-disc list-inside">
            <li><strong>{translate("terms_modal_summary_item_1_title")}:</strong> {translate("terms_modal_summary_item_1_body")}</li>
            <li><strong>{translate("terms_modal_summary_item_2_title")}:</strong> {translate("terms_modal_summary_item_2_body")}</li>
            <li><strong>{translate("terms_modal_summary_item_3_title")}:</strong> {translate("terms_modal_summary_item_3_body")}</li>
            <li><strong>{translate("terms_modal_summary_item_4_title")}:</strong> {translate("terms_modal_summary_item_4_body")}</li>
            <li><strong>{translate("terms_modal_summary_item_5_title")}:</strong> {translate("terms_modal_summary_item_5_body")}</li>
          </ul>

          <p>
            {translate("terms_modal_full_terms_prompt")}{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              {translate("terms_modal_full_terms_link")}
            </Link>.
          </p>
        </div>

        {/* Footer actions */}
        <div className="p-6 pt-4 border-t border-border shrink-0 space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={checked}
              onCheckedChange={(val) => setChecked(val === true)}
              className="mt-0.5 shrink-0"
              id="terms-checkbox"
            />
            <span
              className="text-sm text-muted-foreground hover:text-foreground transition-colors leading-relaxed cursor-pointer"
              onClick={() => setChecked((v) => !v)}
            >
              {translate("terms_modal_checkbox_label")}{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {translate("terms_modal_checkbox_terms_link")}
              </Link>{" "}
              {translate("terms_modal_checkbox_and")}{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {translate("terms_modal_checkbox_privacy_link")}
              </Link>
            </span>
          </div>

          <Button
            onClick={handleAccept}
            disabled={!checked || loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {translate("terms_modal_btn_loading")}
              </>
            ) : (
              translate("terms_modal_btn_continue")
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
